import React, { useEffect, useState } from "react";
import * as Icons from "lucide-react";
import { api, apiError } from "@/lib/api";
import { useOrg } from "@/context/OrgContext";
import { useAuth } from "@/context/AuthContext";
import { PageHeader } from "@/components/common/PageHeader";
import { PageLoading } from "@/components/common/Loading";
import { EmptyState } from "@/components/common/EmptyState";
import { RagBadge } from "@/components/common/RagBadge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { fmtValue, fmtDate, RAG } from "@/lib/format";
import { toast } from "sonner";
import {
  LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, ReferenceLine, CartesianGrid,
} from "recharts";

const CATEGORIES = ["Financial", "Sales", "Marketing", "People", "Operations", "Customer", "Compliance", "Strategic"];
const UNITS = ["€", "%", "Number", "Days", "Hours", "Score", "Custom"];
const FREQ = ["Weekly", "Monthly", "Quarterly", "Annually"];

export default function Scorecard() {
  const { orgId } = useOrg();
  const { canEdit } = useAuth();
  const [kpis, setKpis] = useState(null);
  const [members, setMembers] = useState([]);
  const [detail, setDetail] = useState(null);
  const [creating, setCreating] = useState(false);
  const [catFilter, setCatFilter] = useState("all");

  const load = () => api.get(`/kpis?org_id=${orgId}`).then((r) => setKpis(r.data));
  useEffect(() => {
    if (!orgId) return;
    setKpis(null); load();
    api.get(`/organisations/${orgId}/members`).then((r) => setMembers(r.data));
  }, [orgId]);

  if (!kpis) return <PageLoading />;

  const cats = ["all", ...CATEGORIES.filter((c) => kpis.some((k) => k.category === c))];
  const shown = kpis.filter((k) => catFilter === "all" || k.category === catFilter);
  const grouped = {};
  shown.forEach((k) => { (grouped[k.category] ||= []).push(k); });

  return (
    <div data-testid="scorecard-page">
      <PageHeader
        eyebrow="Performance"
        title="KPI Scorecard"
        subtitle="Track the numbers that matter, against target, with month-on-month trends."
        icon="Gauge"
        actions={canEdit && (
          <Button onClick={() => setCreating(true)} className="bg-[#FFB000] text-[#0B1729] hover:bg-[#E69E00]" data-testid="new-kpi-btn">
            <Icons.Plus size={16} className="mr-1.5" /> New KPI
          </Button>
        )}
      />

      <div className="mb-5 flex flex-wrap gap-2">
        {cats.map((c) => (
          <button
            key={c}
            onClick={() => setCatFilter(c)}
            className={`rounded-full px-3.5 py-1.5 text-sm font-medium transition-colors ${
              catFilter === c ? "bg-[#0B1729] text-white" : "bg-white text-[#1E2D44]/70 hover:bg-[#E4E8EF]"
            }`}
            data-testid={`kpi-cat-${c}`}
          >
            {c === "all" ? "All" : c}
          </button>
        ))}
      </div>

      {shown.length === 0 ? (
        <EmptyState icon="Gauge" title="No KPIs yet" description="Add KPIs to build your performance scorecard."
          action={canEdit && <Button onClick={() => setCreating(true)} className="bg-[#FFB000] text-[#0B1729] hover:bg-[#E69E00]">Add KPI</Button>} />
      ) : (
        <div className="space-y-8">
          {Object.entries(grouped).map(([cat, list]) => (
            <div key={cat}>
              <div className="mb-3 flex items-center gap-2">
                <h2 className="font-display text-lg font-bold text-[#0B1729]">{cat}</h2>
                <span className="text-xs text-[#1E2D44]/45">{list.length}</span>
              </div>
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-3">
                {list.map((k) => (
                  <KpiCard key={k.id} k={k} onClick={() => setDetail(k)} />
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      {detail && <KpiDetail kpi={detail} canEdit={canEdit} onClose={() => setDetail(null)} onSaved={() => { setDetail(null); load(); }} />}
      {creating && <CreateKpiDialog orgId={orgId} members={members} onClose={() => setCreating(false)} onSaved={() => { setCreating(false); load(); }} />}
    </div>
  );
}

function KpiCard({ k, onClick }) {
  const rag = k.status || "grey";
  const trendIcon = k.trend === "up" ? Icons.TrendingUp : k.trend === "down" ? Icons.TrendingDown : Icons.Minus;
  const TrendIcon = trendIcon;
  return (
    <div onClick={onClick} className="amden-card amden-card-hover cursor-pointer p-5" data-testid={`kpi-card-${k.id}`}>
      <div className="flex items-start justify-between">
        <div>
          <div className="text-sm font-semibold text-[#0B1729]">{k.name}</div>
          <div className="text-xs text-[#1E2D44]/50">{k.frequency}</div>
        </div>
        <span className={`flex h-7 w-7 items-center justify-center rounded-full ${RAG[rag].bg}`}>
          <TrendIcon size={14} className={RAG[rag].text} />
        </span>
      </div>
      <div className="mt-4 flex items-end justify-between">
        <div>
          <div className="font-display text-2xl font-bold text-[#0B1729]">{fmtValue(k.current_value, k.unit)}</div>
          <div className="text-xs text-[#1E2D44]/50">Target {fmtValue(k.target, k.unit)}</div>
        </div>
        <RagBadge rag={rag}>{rag === "green" ? "On track" : rag === "amber" ? "Watch" : rag === "red" ? "Off target" : "—"}</RagBadge>
      </div>
    </div>
  );
}

function KpiDetail({ kpi, canEdit, onClose, onSaved }) {
  const [full, setFull] = useState(null);
  const [period, setPeriod] = useState("");
  const [value, setValue] = useState("");
  const [commentary, setCommentary] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => { api.get(`/kpis/${kpi.id}`).then((r) => setFull(r.data)); }, [kpi.id]);

  const addResult = async () => {
    if (!period || value === "") return toast.error("Enter a period and value");
    setSaving(true);
    try {
      await api.post(`/kpis/${kpi.id}/results`, { period, value: parseFloat(value), commentary });
      toast.success("Result recorded");
      onSaved();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const chartData = (full?.results || []).map((r) => ({ name: r.period, value: r.value }));

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-h-[90vh] max-w-2xl overflow-y-auto">
        <DialogHeader>
          <div className="flex items-center gap-2">
            <RagBadge rag={kpi.status}>{kpi.category}</RagBadge>
            <span className="text-xs text-[#1E2D44]/50">Owner: {kpi.owner_name || "—"}</span>
          </div>
          <DialogTitle className="font-display text-xl">{kpi.name}</DialogTitle>
        </DialogHeader>

        <div className="space-y-5">
          <div className="grid grid-cols-3 gap-3">
            <div className="rounded-xl bg-[#F4F6F9] p-3"><div className="label-eyebrow mb-1">Current</div><div className="font-display text-lg font-bold">{fmtValue(kpi.current_value, kpi.unit)}</div></div>
            <div className="rounded-xl bg-[#F4F6F9] p-3"><div className="label-eyebrow mb-1">Target</div><div className="font-display text-lg font-bold">{fmtValue(kpi.target, kpi.unit)}</div></div>
            <div className="rounded-xl bg-[#F4F6F9] p-3"><div className="label-eyebrow mb-1">Previous</div><div className="font-display text-lg font-bold">{fmtValue(kpi.previous_value, kpi.unit)}</div></div>
          </div>

          {chartData.length > 1 && (
            <div className="h-56 rounded-xl border border-[#E4E8EF] p-4">
              <div className="label-eyebrow mb-2">Trend vs target</div>
              <ResponsiveContainer width="100%" height="85%">
                <LineChart data={chartData} margin={{ top: 5, right: 10, left: -18, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#EEF1F6" vertical={false} />
                  <XAxis dataKey="name" tick={{ fontSize: 11, fill: "#8A97AB" }} axisLine={false} tickLine={false} />
                  <YAxis tick={{ fontSize: 11, fill: "#8A97AB" }} axisLine={false} tickLine={false} />
                  <Tooltip contentStyle={{ borderRadius: 10, border: "1px solid #E4E8EF", fontSize: 12 }} />
                  {kpi.target != null && <ReferenceLine y={kpi.target} stroke="#FFB000" strokeDasharray="4 4" />}
                  <Line type="monotone" dataKey="value" stroke="#0B1729" strokeWidth={2.5} dot={{ r: 3, fill: "#FFB000" }} />
                </LineChart>
              </ResponsiveContainer>
            </div>
          )}

          {kpi.commentary && <div className="rounded-lg bg-[#F4F6F9] p-3 text-sm text-[#1E2D44]/75"><span className="font-medium">Commentary: </span>{kpi.commentary}</div>}

          {full?.results?.length > 0 && (
            <div>
              <div className="label-eyebrow mb-2">History</div>
              <div className="max-h-40 overflow-y-auto rounded-lg border border-[#E4E8EF]">
                {[...full.results].reverse().map((r) => (
                  <div key={r.id} className="flex items-center justify-between border-b border-[#EEF1F6] px-3 py-2 text-sm last:border-0">
                    <span className="text-[#1E2D44]/60">{r.period}</span>
                    <span className="font-medium">{fmtValue(r.value, kpi.unit)}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {canEdit && (
            <div className="rounded-xl border border-[#E4E8EF] p-4">
              <div className="label-eyebrow mb-2">Record new result</div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label className="text-xs">Period</Label><Input value={period} onChange={(e) => setPeriod(e.target.value)} placeholder="e.g. Jul 2026" className="mt-1" data-testid="kpi-period-input" /></div>
                <div><Label className="text-xs">Value</Label><Input type="number" value={value} onChange={(e) => setValue(e.target.value)} className="mt-1" data-testid="kpi-value-input" /></div>
              </div>
              <Textarea value={commentary} onChange={(e) => setCommentary(e.target.value)} placeholder="Commentary (optional)" className="mt-3" />
              <Button onClick={addResult} disabled={saving} className="mt-3 w-full bg-[#FFB000] text-[#0B1729] hover:bg-[#E69E00]" data-testid="save-kpi-result">
                {saving ? <Icons.Loader2 className="animate-spin" size={16} /> : "Record result"}
              </Button>
            </div>
          )}
        </div>
        <DialogFooter><Button variant="outline" onClick={onClose}>Close</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function CreateKpiDialog({ orgId, members, onClose, onSaved }) {
  const [form, setForm] = useState({
    name: "", category: "Sales", unit: "Number", target: "", current_value: "",
    frequency: "Monthly", direction: "up", warning_threshold: "", owner_id: "", commentary: "",
  });
  const [saving, setSaving] = useState(false);
  const save = async () => {
    if (!form.name) return toast.error("Add a KPI name");
    setSaving(true);
    try {
      await api.post(`/kpis?org_id=${orgId}`, {
        ...form,
        target: form.target === "" ? null : parseFloat(form.target),
        current_value: form.current_value === "" ? null : parseFloat(form.current_value),
        warning_threshold: form.warning_threshold === "" ? null : parseFloat(form.warning_threshold),
        owner_id: form.owner_id || null,
      });
      toast.success("KPI created");
      onSaved();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-h-[90vh] max-w-lg overflow-y-auto">
        <DialogHeader><DialogTitle>New KPI</DialogTitle></DialogHeader>
        <div className="space-y-3.5">
          <div><Label>KPI name</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1" data-testid="new-kpi-name" /></div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Category</Label>
              <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                <SelectTrigger className="mt-1" data-testid="new-kpi-category"><SelectValue /></SelectTrigger>
                <SelectContent>{CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>Unit</Label>
              <Select value={form.unit} onValueChange={(v) => setForm({ ...form, unit: v })}>
                <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                <SelectContent>{UNITS.map((u) => <SelectItem key={u} value={u}>{u}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>Target</Label><Input type="number" value={form.target} onChange={(e) => setForm({ ...form, target: e.target.value })} className="mt-1" /></div>
            <div><Label>Current value</Label><Input type="number" value={form.current_value} onChange={(e) => setForm({ ...form, current_value: e.target.value })} className="mt-1" /></div>
            <div><Label>Frequency</Label>
              <Select value={form.frequency} onValueChange={(v) => setForm({ ...form, frequency: v })}>
                <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                <SelectContent>{FREQ.map((f) => <SelectItem key={f} value={f}>{f}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>Direction of success</Label>
              <Select value={form.direction} onValueChange={(v) => setForm({ ...form, direction: v })}>
                <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="up">Higher is better</SelectItem><SelectItem value="down">Lower is better</SelectItem></SelectContent>
              </Select>
            </div>
          </div>
          <div><Label>Warning threshold (optional)</Label><Input type="number" value={form.warning_threshold} onChange={(e) => setForm({ ...form, warning_threshold: e.target.value })} className="mt-1" /></div>
          <div><Label>Owner</Label>
            <Select value={form.owner_id} onValueChange={(v) => setForm({ ...form, owner_id: v })}>
              <SelectTrigger className="mt-1"><SelectValue placeholder="Assign" /></SelectTrigger>
              <SelectContent>{members.map((m) => <SelectItem key={m.id} value={m.id}>{m.name}</SelectItem>)}</SelectContent>
            </Select>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={save} disabled={saving} className="bg-[#FFB000] text-[#0B1729] hover:bg-[#E69E00]" data-testid="save-new-kpi">{saving ? <Icons.Loader2 className="animate-spin" size={16} /> : "Create KPI"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
